import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowRight,
  CalendarDays,
  ChevronDown,
  ChevronRight,
  History,
  MapPin,
  MousePointerClick,
  Pencil,
  Plus,
  Minus,
  Route as RouteIcon,
  Trash2
} from 'lucide-react';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import { actionBadge, displayValue } from '../../utils/commandCenterHelpers';

// Per-action visual language shared by the summary chips and list accents so a
// colour always means the same action everywhere on the card.
const ACTION_META = {
  create:   { label: 'Created',     hex: '#059669', chip: 'text-emerald-700 bg-emerald-50 border-emerald-200', dot: 'bg-emerald-500' },
  update:   { label: 'Updated',     hex: '#4f46e5', chip: 'text-brand-700 bg-brand-50 border-brand-200',       dot: 'bg-brand-500' },
  import:   { label: 'Imported',    hex: '#d97706', chip: 'text-amber-700 bg-amber-50 border-amber-200',       dot: 'bg-amber-500' },
  delete:   { label: 'Deleted',     hex: '#dc2626', chip: 'text-rose-700 bg-rose-50 border-rose-200',          dot: 'bg-rose-500' },
  rollback: { label: 'Rolled back', hex: '#0891b2', chip: 'text-cyan-700 bg-cyan-50 border-cyan-200',          dot: 'bg-cyan-500' },
  baseline: { label: 'Baseline',    hex: '#64748b', chip: 'text-slate-600 bg-slate-50 border-slate-200',       dot: 'bg-slate-400' }
};

const actionMeta = (action) => ACTION_META[action] || ACTION_META.baseline;

const fmtTime = (iso) =>
  new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });

// One calendar day of this user's activity — expands into a summary, a type
// filter, and a master list + detail panel with Edit / Rollback / Delete.
export default function DayActivityCard({ day, expanded, onToggle, onEdit, onReactivate, onDelete }) {
  const total = day.stops.length + day.routes.length;

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-soft overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50/70 transition-colors text-left"
      >
        {expanded ? (
          <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 text-slate-400 shrink-0" />
        )}
        <span className="h-9 w-9 rounded-lg bg-brand-50 text-brand-600 inline-flex items-center justify-center shrink-0">
          <CalendarDays className="h-4.5 w-4.5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[15px] font-semibold text-slate-900">{day.dateKey}</p>
          <p className="text-[12px] text-slate-500">
            {total} change{total === 1 ? '' : 's'}
          </p>
        </div>
        <div className="hidden sm:flex items-center gap-2 shrink-0">
          {day.stops.length > 0 && (
            <Badge variant="outline" size="sm" className="tabular-nums">
              {day.stops.length} stop{day.stops.length === 1 ? '' : 's'}
            </Badge>
          )}
          {day.routes.length > 0 && (
            <Badge variant="outline" size="sm" className="tabular-nums">
              {day.routes.length} route{day.routes.length === 1 ? '' : 's'}
            </Badge>
          )}
        </div>
      </button>

      {expanded && (
        <ExpandedDay day={day} onEdit={onEdit} onReactivate={onReactivate} onDelete={onDelete} />
      )}
    </div>
  );
}

function ExpandedDay({ day, onEdit, onReactivate, onDelete }) {
  const [filter, setFilter] = useState('all'); // all | stop | route
  const [selectedId, setSelectedId] = useState(null);

  // Newest-first combined list, tagged so we can filter / icon by type.
  const items = useMemo(() => {
    const stops = day.stops.map((e) => ({ ...e, kind: 'stop' }));
    const routes = day.routes.map((e) => ({ ...e, kind: 'route' }));
    return [...stops, ...routes].sort((a, b) => new Date(b.savedAt) - new Date(a.savedAt));
  }, [day]);

  const filtered = useMemo(
    () => (filter === 'all' ? items : items.filter((e) => e.kind === filter)),
    [items, filter]
  );

  // Keep a valid selection as the filter changes.
  useEffect(() => {
    if (!filtered.length) {
      setSelectedId(null);
    } else if (!filtered.some((e) => e.id === selectedId)) {
      setSelectedId(filtered[0].id);
    }
  }, [filtered, selectedId]);

  const selected = filtered.find((e) => e.id === selectedId) || null;

  return (
    <div className="border-t border-slate-100 bg-slate-50/50 px-4 py-4 space-y-4">
      <SummaryStrip items={items} stops={day.stops.length} routes={day.routes.length} />

      <FilterPills
        filter={filter}
        onChange={setFilter}
        counts={{ all: items.length, stop: day.stops.length, route: day.routes.length }}
      />

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* master list */}
        <div className="lg:col-span-2 rounded-xl border border-slate-200 bg-white overflow-hidden">
          <ul className="max-h-[460px] overflow-auto divide-y divide-slate-100">
            {filtered.map((e) => (
              <MasterRow
                key={e.id}
                event={e}
                selected={e.id === selectedId}
                onSelect={() => setSelectedId(e.id)}
              />
            ))}
            {filtered.length === 0 && (
              <li className="px-3 py-8 text-center text-[12.5px] text-slate-400">Nothing here.</li>
            )}
          </ul>
        </div>

        {/* detail */}
        <div className="lg:col-span-3">
          <DetailPanel
            event={selected}
            onEdit={onEdit}
            onReactivate={onReactivate}
            onDelete={onDelete}
          />
        </div>
      </div>
    </div>
  );
}

// ── Summary ────────────────────────────────────────────────────────────────

function SummaryStrip({ items, stops, routes }) {
  const counts = items.reduce((acc, e) => {
    const key = !e.exists || e.action === 'delete' ? 'delete' : e.action;
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  const order = ['create', 'update', 'import', 'rollback', 'delete', 'baseline'];
  const chips = order.filter((k) => counts[k]);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 text-white px-2.5 py-1 text-[12px] font-semibold tabular-nums">
        {items.length} change{items.length === 1 ? '' : 's'}
      </span>
      <span className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-[12px] text-slate-600">
        <MapPin className="h-3.5 w-3.5 text-slate-400" /> {stops} stop{stops === 1 ? '' : 's'}
      </span>
      <span className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-[12px] text-slate-600">
        <RouteIcon className="h-3.5 w-3.5 text-slate-400" /> {routes} route{routes === 1 ? '' : 's'}
      </span>
      <span className="mx-0.5 h-4 w-px bg-slate-200" />
      {chips.map((k) => {
        const m = actionMeta(k);
        return (
          <span
            key={k}
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[12px] font-medium tabular-nums ${m.chip}`}
          >
            <span className={`h-1.5 w-1.5 rounded-full ${m.dot}`} />
            {counts[k]} {m.label}
          </span>
        );
      })}
    </div>
  );
}

// ── Filter pills ─────────────────────────────────────────────────────────────

function FilterPills({ filter, onChange, counts }) {
  const pills = [
    { key: 'all', label: 'All', icon: null },
    { key: 'stop', label: 'Stops', icon: MapPin },
    { key: 'route', label: 'Routes', icon: RouteIcon }
  ];
  return (
    <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5">
      {pills.map((p) => {
        const active = filter === p.key;
        return (
          <button
            key={p.key}
            type="button"
            onClick={() => onChange(p.key)}
            disabled={p.key !== 'all' && counts[p.key] === 0}
            className={
              'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12.5px] font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ' +
              (active ? 'bg-brand-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-800')
            }
          >
            {p.icon && <p.icon className="h-3.5 w-3.5" />}
            {p.label}
            <span className={'tabular-nums ' + (active ? 'text-white/80' : 'text-slate-400')}>
              {counts[p.key]}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ── Master list row ──────────────────────────────────────────────────────────

function MasterRow({ event, selected, onSelect }) {
  const isDeleted = event.action === 'delete' || !event.exists;
  const m = actionMeta(isDeleted ? 'delete' : event.action);
  const Icon = event.kind === 'route' ? RouteIcon : MapPin;
  const meta =
    event.kind === 'route'
      ? [event.detail?.routeNumber != null && `#${event.detail.routeNumber}`, event.detail?.direction]
          .filter(Boolean)
          .join(' · ')
      : event.detail?.stopId || '';

  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        style={{ borderLeftColor: m.hex }}
        className={
          'w-full text-left flex items-center gap-2.5 px-3 py-2.5 border-l-[3px] transition-colors ' +
          (selected ? 'bg-brand-50/70' : 'hover:bg-slate-50')
        }
      >
        <span className="h-7 w-7 rounded-lg bg-slate-100 text-slate-500 inline-flex items-center justify-center shrink-0">
          <Icon className="h-3.5 w-3.5" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[13px] font-semibold text-slate-900 truncate">{event.name}</span>
          <span className="block text-[11px] text-slate-400 font-mono truncate">
            {meta}
            {meta && ' · '}
            {fmtTime(event.savedAt)}
          </span>
        </span>
        <span className={`h-2 w-2 rounded-full shrink-0 ${m.dot}`} title={m.label} />
      </button>
    </li>
  );
}

// ── Detail panel ──────────────────────────────────────────────────────────────

function DetailPanel({ event, onEdit, onReactivate, onDelete }) {
  if (!event) {
    return (
      <div className="rounded-xl border border-dashed border-slate-200 bg-white h-full min-h-[160px] flex flex-col items-center justify-center text-center">
        <MousePointerClick className="h-5 w-5 text-slate-300 mb-1.5" />
        <p className="text-[12.5px] text-slate-500">Select a change to see the details.</p>
      </div>
    );
  }

  const badge = actionBadge(event.action);
  const isDeleted = event.action === 'delete' || !event.exists;
  const changes = event.changeLogs || [];
  const isRoute = event.kind === 'route';

  const fields = isRoute
    ? [
        ['Route no.', event.detail?.routeNumber != null ? `#${event.detail.routeNumber}` : '—'],
        ['Direction', event.detail?.direction || '—'],
        ['Stops', event.detail?.stopCount ?? 0],
        ['Time', fmtTime(event.savedAt)]
      ]
    : [
        ['Stop ID', event.detail?.stopId || '—'],
        [
          'Coordinates',
          event.detail?.lat != null && event.detail?.lng != null
            ? `${Number(event.detail.lat).toFixed(5)}, ${Number(event.detail.lng).toFixed(5)}`
            : '—'
        ],
        ['Time', fmtTime(event.savedAt)]
      ];

  return (
    <div className="rounded-xl border border-slate-200 bg-white">
      <div className="px-4 py-3 border-b border-slate-100 flex items-start gap-3">
        <span className="h-9 w-9 rounded-lg bg-slate-100 text-slate-600 inline-flex items-center justify-center shrink-0">
          {isRoute ? <RouteIcon className="h-4.5 w-4.5" /> : <MapPin className="h-4.5 w-4.5" />}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[14.5px] font-semibold text-slate-900 truncate">{event.name}</p>
          <span className="mt-1 flex items-center gap-1.5 flex-wrap">
            <Badge variant={badge.variant} size="sm">{badge.label}</Badge>
            {isDeleted && <Badge variant="danger" size="sm">Deleted</Badge>}
            {event.isCurrent && !isDeleted && <Badge variant="success" size="sm">Current</Badge>}
          </span>
        </div>
      </div>

      <div className="px-4 py-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
        {fields.map(([label, value]) => (
          <div key={label}>
            <p className="text-[10.5px] uppercase tracking-wide text-slate-400">{label}</p>
            <p className="text-[12.5px] text-slate-800 font-medium truncate">{value}</p>
          </div>
        ))}
      </div>

      {changes.length > 0 && (
        <div className="px-4 pb-3.5">
          <p className="text-[10.5px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">
            What changed
          </p>
          <ul className="space-y-1.5 rounded-lg border border-slate-100 bg-slate-50/60 px-3 py-2.5">
            {changes.map((log) => (
              <ChangeLine key={log.id} log={log} />
            ))}
          </ul>
        </div>
      )}

      {/* actions */}
      <div className="px-4 py-3 border-t border-slate-100 flex flex-wrap items-center gap-2">
        {isDeleted ? (
          <span className="text-[12px] text-slate-400">
            This version is deleted — restore it from Trash to edit.
          </span>
        ) : (
          <>
            <Button size="sm" variant="outline" onClick={() => onEdit(event)} leftIcon={Pencil}>
              Edit
            </Button>
            {!event.isCurrent && (
              <Button size="sm" variant="ghost" onClick={() => onReactivate(event)} leftIcon={History}>
                Rollback
              </Button>
            )}
            {onDelete && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onDelete(event)}
                leftIcon={Trash2}
                className="text-rose-600 hover:bg-rose-50 hover:text-rose-700 ml-auto"
              >
                Delete
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function ChangeLine({ log }) {
  const label = log.refName || log.fieldChanged || '';

  if (log.changeType === 'added') {
    return (
      <li className="flex items-center gap-1.5 text-[12.5px] text-emerald-700">
        <Plus className="h-3 w-3 shrink-0" />
        <span className="truncate">{label || displayValue(log.newValue)}</span>
      </li>
    );
  }

  if (log.changeType === 'removed') {
    return (
      <li className="flex items-center gap-1.5 text-[12.5px] text-rose-700">
        <Minus className="h-3 w-3 shrink-0" />
        <span className="truncate">{label || displayValue(log.oldValue)}</span>
      </li>
    );
  }

  // changed / reactivated → show old → new
  return (
    <li className="flex items-center gap-2 text-[12.5px] flex-wrap">
      <span className="font-medium text-slate-700">{log.fieldChanged || 'Changed'}:</span>
      <span className="text-slate-400 line-through">{displayValue(log.oldValue)}</span>
      <ArrowRight className="h-3 w-3 text-slate-300 shrink-0" />
      <span className="text-slate-900 font-medium">{displayValue(log.newValue)}</span>
    </li>
  );
}
